/**
 * Color utility functions for theme-aware colors and accessibility.
 */

/**
 * Calculate relative luminance of a color.
 * Uses the sRGB color space formula from WCAG 2.1.
 */
function getLuminance(hex: string): number {
	// Remove # if present
	const cleanHex = hex.replace('#', '')

	// Parse RGB values
	const r = parseInt(cleanHex.slice(0, 2), 16) / 255
	const g = parseInt(cleanHex.slice(2, 4), 16) / 255
	const b = parseInt(cleanHex.slice(4, 6), 16) / 255

	// Apply sRGB gamma correction
	const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))

	const rLinear = toLinear(r)
	const gLinear = toLinear(g)
	const bLinear = toLinear(b)

	// Calculate luminance
	return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear
}

/**
 * Get accessible text color (black or white) for a given background color.
 * Uses WCAG contrast ratio guidelines.
 *
 * @param backgroundColor - Hex color string (with or without #)
 * @returns 'black' or 'white' for optimal contrast
 */
export function getTextColorForBackground(backgroundColor: string): 'black' | 'white' {
	try {
		const luminance = getLuminance(backgroundColor)
		// Use 0.179 as threshold (matches WCAG 4.5:1 contrast ratio)
		return luminance > 0.179 ? 'black' : 'white'
	} catch {
		// Default to black if color parsing fails
		return 'black'
	}
}

/**
 * Adjust a color for the current theme.
 * In dark mode, lightens dark colors for better visibility.
 *
 * @param color - Hex color string
 * @param theme - Current theme ('light' or 'dark')
 * @returns Adjusted hex color string
 */
export function getThemeAwareColor(color: string, theme: 'light' | 'dark'): string {
	if (theme === 'light') {
		return color
	}

	try {
		const luminance = getLuminance(color)

		// If color is very dark in dark mode, lighten it for visibility
		if (luminance < 0.1) {
			const cleanHex = color.replace('#', '')
			const r = Math.min(255, parseInt(cleanHex.slice(0, 2), 16) + 60)
			const g = Math.min(255, parseInt(cleanHex.slice(2, 4), 16) + 60)
			const b = Math.min(255, parseInt(cleanHex.slice(4, 6), 16) + 60)

			return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
		}

		return color
	} catch {
		return color
	}
}
